import {
  Permission,
  PermissionAction,
  PermissionNameEnum,
} from '@Types/permissions';
import config from 'src/config';
import { MenuOption, ValidProps } from '@Types/index';
import { IS_UAE } from './const';

export const compareDesc = (propertyName: string) => {
  return function (a: any, b: any) {
    const value1 = a[propertyName].toUpperCase();
    const value2 = b[propertyName].toUpperCase();
    if (value2 < value1) {
      return -1;
    } else if (value2 > value1) {
      return 1;
    } else {
      return 0;
    }
  };
};

export const getCurrentSelectedOrgId = (allOrgs: MenuOption[]) => {
  const sessionOrgId = localStorage.getItem('casCurSelectedOrgId');
  if (sessionOrgId && allOrgs.find((item) => item.key === sessionOrgId)) {
    return sessionOrgId;
  } else {
    const commonOrgs = allOrgs.find((item) => item.key !== 'all');
    if (commonOrgs) {
      return commonOrgs?.key;
    }
  }
  return null;
};

export function extractEmail(str: string) {
  const regex = /<([^>]+)>/;
  const match = str.match(regex);
  return match ? match[1] : str;
}

export const getDefaultSingleOrgId = (allOrgs: MenuOption[]) => {
  const sessionOrgId = localStorage.getItem('casCurSelectedOrgId');
  if (
    sessionOrgId &&
    sessionOrgId !== 'all' &&
    allOrgs.find((item) => item.key === sessionOrgId)
  ) {
    return sessionOrgId;
  } else {
    const commonOrgs = allOrgs.find((item) => item.key !== 'all');
    if (commonOrgs) {
      return commonOrgs?.key;
    }
  }
  return null;
};

export const checkPassword = function (p: string) {
  if (!p) return false;
  if (p.length < 8) return false;
  if (!/[~!@#$%^*-+=|{}[\]:;,.<>?]/.test(p)) return false;
  if (!/[0-9]/.test(p)) return false;
  if (!/[a-z]/.test(p)) return false;
  if (!/[A-Z]/.test(p)) return false;
  return true;
};

export function getCookie(cookieName: string) {
  const list: { [key: string]: string } = {};
  const rc = document.cookie;
  if (rc) {
    rc.split(';').forEach((cookie) => {
      const parts = cookie.split('=');
      const key = parts.shift()?.trim();
      if (key !== undefined) {
        list[key] = decodeURIComponent(parts.join('='));
      }
    });
  }
  return list[cookieName];
}

export const isInUIC = () => {
  if (
    localStorage.getItem('uic_companyGUID') !== null ||
    localStorage.getItem('uic-token') !== null
  ) {
    return true;
  }
  return false;
};

export const isEUOrUKSite = () => {
  return ['prod_we', 'prod_uks'].includes(config.ui.siteName);
};

export const setLocalStorageItemWithExpiry = (
  key: string,
  value: any,
  millisecondTtl: number
) => {
  const now = new Date();
  const item = {
    value,
    expiry: now.getTime() + millisecondTtl,
  };
  localStorage.setItem(key, JSON.stringify(item));
};

export const getLocalStorageItemWithExpiry = (key: string): null | any => {
  const itemStr = localStorage.getItem(key);
  if (!itemStr) {
    return null;
  }
  const item = JSON.parse(itemStr);
  const now = new Date();
  if (now.getTime() > item.expiry) {
    localStorage.removeItem(key);
    return null;
  }
  return item.value;
};

export function setFavicon() {
  const link: any =
    document.querySelector("link[rel*='icon']") ||
    document.createElement('link');
  document.title = IS_UAE
    ? 'Cyber Eye Cloud Service Security'
    : 'Trend Micro™ Cloud App Security';
  link.type = 'image/png';
  link.rel = 'shortcut icon';
  link.href = IS_UAE
    ? +`${config.ui.prefix}/cyber-favicon.png`
    : `${config.ui.prefix}/favicon.png`;
  document.getElementsByTagName('head')[0].appendChild(link);
}

export const isDefaultUI = () => {
  const pathPrefix = window.location.pathname.split('/')[1];
  if (pathPrefix === 'preview') {
    return false;
  }
  return true;
};

export const getValidateMsg = (validates: ValidProps[], value: string) => {
  let isValid = true;
  for (const valid of validates) {
    if (!valid.validMethod(value)) {
      isValid = false;
      return valid.errMsg;
    }
  }
  if (isValid) {
    return undefined;
  }
};

export const handleLinkClick = (e: any) => {
  e.preventDefault();
  let href = e.currentTarget.getAttribute('href');
  if (href.startsWith('/')) {
    href = href.slice(1);
  }
  const target = e.currentTarget.getAttribute('target')
    ? e.currentTarget.getAttribute('target')
    : '_self';
  if (isInUIC()) {
    if (target === '_blank') {
      window.open(`/#${config.ui.uicPrefix}/${href}`, '_blank');
    } else {
      (window.parent as any).APP.$router.push(`${config.ui.uicPrefix}/${href}`);
    }
  } else {
    window.open(`${config.ui.prefix}/${href}`, target);
  }
};

export const handleRedirectClick = (href: string, target?: string) => {
  if (href.startsWith('/')) {
    href = href.slice(1);
  }
  const targetValue = target ? target : '_self';
  if (isInUIC()) {
    if (target === '_blank') {
      window.open(
        `/#${config.ui.uicPrefix}/${href}`,
        '_blank',
        'noopener, noreferrer'
      );
    } else {
      (window.parent as any).APP.$router.push(`${config.ui.uicPrefix}/${href}`);
    }
  } else {
    window.open(`${config.ui.prefix}/${href}`, targetValue);
  }
};

export const safeDecodeURIComponent = (str: string) => {
  try {
    return decodeURIComponent(str);
  } catch (e) {
    console.error(`Error decoding string: ${str}`, e);
    return str;
  }
};

/**
 * @description: just replace the top URL
 * @deprecated use navigateWithHandler in hooks.ts instead
 */
export const handleNavigate = (href: string) => {
  if (!isInUIC()) return;

  if (href.startsWith('/')) {
    href = href.slice(1);
  }

  if (window.top) {
    const address = new URL(
      `${window.top.location.origin}/#/app/email-and-collaboration/${href}`
    );
    window.top.history.replaceState({}, '', address);
  }
};

export const changeBreadCrumbs = (
  breadCrumbInfo: { breadCrumb: string; router: string }[]
) => {
  const breadCrumbs = breadCrumbInfo.map((item) => {
    return {
      breadcrumbname: item.breadCrumb,
      router: item.router,
      preview: false,
      comingsoon: false,
    };
  });

  if ((window.parent as any).APP && (window.parent as any).APP.$eventBus) {
    (window.parent as any).APP.$eventBus.$emit(
      'changeBreadCrumbs',
      breadCrumbs
    );
  }
};

export function exportFile(fileName: string, content: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.download = fileName;
  link.href = url;
  link.click();
}

export function parseCSVToJSON(
  csvContent: string,
  fields: string[] | undefined
) {
  const lines = csvContent.split('\n');
  const headers = lines[0].split(',');
  const jsonData = [];

  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',');
    if (values.length !== headers.length) {
      continue; // 跳过不完整的行
    }

    const entry: any = {};
    for (let j = 0; j < headers.length; j++) {
      const value = values[j].replace(/"/g, ''); // 去除双引号

      if (fields && headers.length === fields.length) {
        const header = fields[j]; // 去除双引号
        entry[header] = value;
      } else {
        const header = headers[j].replace(/"/g, ''); // 去除双引号
        entry[header] = value;
      }
    }

    jsonData.push(entry);
  }

  return jsonData;
}

export const compareAsc = (propertyName: string) => {
  return function (a: any, b: any) {
    const value1 = a[propertyName].toUpperCase();
    const value2 = b[propertyName].toUpperCase();
    if (value2 < value1) {
      return 1;
    } else if (value2 > value1) {
      return -1;
    } else {
      return 0;
    }
  };
};

export function computePercent(last: number, prev: number) {
  if (prev === 0) {
    return last === 0 ? 0 : 1;
  }
  return (last - prev) / prev;
}

export function toolTipString(sourceString: string | undefined, len: number) {
  sourceString = sourceString + '';
  if (sourceString.length < len) {
    return sourceString;
  }
  return sourceString.substring(0, len).concat('...');
}

export function urlMask(url: string, judge?: boolean): string {
  if (!url || typeof url === 'undefined') return url;
  if (judge) {
    if (
      url.indexOf('http://') < 0 &&
      url.indexOf('https://') < 0 &&
      url.indexOf('www') < 0
    ) {
      return url;
    }
  }
  url = url
    .replace(/http/g, '[http]')
    .replace(/\[http\]s/g, '[https]')
    .replace(/\//g, '[/]')
    .replace(/\./g, '[.]');
  return url;
}

export function decodeUrlMask(maskedUrl: string) {
  if (!maskedUrl || typeof maskedUrl === 'undefined') return maskedUrl;

  maskedUrl = maskedUrl
    .replace(/\[http\]/g, 'http')
    .replace(/\[https\]/g, 'https')
    .replace(/\[\/\]/g, '/')
    .replace(/\[\.\]/g, '.');

  return maskedUrl;
}

export function hash(str: string) {
  let hash = 0,
    i,
    chr;
  if (!str || str.length === 0) return hash;
  for (i = 0; i < str.length; i++) {
    chr = str.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
}

export const formatNumberWithUnit = function (strNum: string) {
  const UNITS = ['', ' B', ' T', ' Q'];
  if (typeof strNum === 'undefined') {
    strNum = '';
  }
  let tmp_str = strNum + '';
  const ret: any = {};
  ret.tooltip = tmp_str.replace(/\B(?=(?:\d{3})+(?!\d))/g, ',');
  if (tmp_str.length <= 9) {
    ret.dispstring = ret.tooltip;
  } else {
    let index = Math.ceil((tmp_str.length - 9) / 3);
    if (index > 3) index = 3;

    if (tmp_str.length <= 18) {
      ret.dispstring =
        tmp_str.substring(0, tmp_str.length - (9 + (index - 1) * 3)) +
        '.' +
        tmp_str.substring(tmp_str.length - (9 + (index - 1) * 3), 2) +
        UNITS[index];
    } else {
      tmp_str = tmp_str.substring(0, tmp_str.length - (9 + (index - 1) * 3));
      ret.dispstring =
        tmp_str.replace(/\B(?=(?:\d{3})+(?!\d))/g, ',') + UNITS[index];
    }
  }
  return ret;
};
const EmailReg =
  /^[a-z0-9!#$%&'*+\/=?^_|}~-]+(?:\.[a-z0-9!#$%&'*+\/=?^_|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i; /* eslint-disable-line */
export function validateEmail(email: string, required?: boolean) {
  if (!email) {
    if (required) return false;
    return true;
  }
  return EmailReg.test(email);
}

export function validateEmailsWithSemicolon(email: string, required?: boolean) {
  if (!email) {
    if (required) return false;
    return true;
  }
  if (email.includes(';')) {
    let flag = true;
    const emails = email.split(';');
    emails.map((item) => {
      if (!EmailReg.test(item)) {
        flag = false;
      }
    });
    return flag;
  } else {
    return EmailReg.test(email);
  }
}

const IPv4Reg = /^((25[0-5]|2[0-4]\d|[01]?\d\d?)($|(?!\.$)\.)){4}$/;
export function validateIPv4(ip: string) {
  return IPv4Reg.test(ip);
}

export function validateSpoUrl(address: string): boolean {
  const urlReg =
    //eslint-disable-next-line no-useless-escape
    /^(https?|ftp):\/\/(((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:)*@)?(((\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5]))|((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.?)(:\d*)?)(\/((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)+(\/(([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)*)*)?)?(\?((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)|[\uE000-\uF8FF]|\/|\?)*)?(\#((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)|\/|\?)*)?$/i;
  const spoSpeicalChar = /[#%*:<>?\\|]/;
  const split = address.lastIndexOf('/', address.length - 2);
  const preUrl = address.slice(0, split + 1);
  const postUrl = address.slice(split + 1, address.length - 1);

  if (preUrl === 'https://') {
    if (!urlReg.test(address)) {
      return false;
    }
  } else {
    if (!urlReg.test(preUrl)) {
      return false;
    }
    if (!postUrl || postUrl === '' || spoSpeicalChar.test(postUrl)) {
      return false;
    }
  }
  return true;
}

export function fillZeroToString(number: string | number): string {
  if (typeof number === 'number') {
    return number < 10 ? '0' + number : '' + number;
  } else if (number) {
    return number.length > 1 ? number : '0' + number;
  }
  return '00';
}

export function withEditPermission(
  permissionName: PermissionNameEnum[] | PermissionNameEnum,
  permissions: Permission | undefined
) {
  if (Array.isArray(permissionName)) {
    for (const name of permissionName) {
      if (
        permissions &&
        permissions[name] &&
        permissions[name].includes(PermissionAction.Editable)
      ) {
        return true;
      }
    }
    return false;
  } else {
    if (
      permissions &&
      permissions[permissionName] &&
      permissions[permissionName].includes(PermissionAction.Editable)
    ) {
      return true;
    }
    return false;
  }
}

export function withReadPermission(
  permissionName: PermissionNameEnum[] | PermissionNameEnum,
  permissions: Permission | undefined
) {
  if (Array.isArray(permissionName)) {
    for (const name of permissionName) {
      if (
        permissions &&
        permissions[name] &&
        permissions[name].includes(PermissionAction.ViewOnly)
      ) {
        return true;
      }
    }
    return false;
  } else {
    if (
      permissions &&
      permissions[permissionName] &&
      permissions[permissionName].includes(PermissionAction.ViewOnly)
    ) {
      return true;
    }
    return false;
  }
}

export function flattenRoutes(routes: any[]): any[] {
  const newRoutes = [];
  for (const route of routes) {
    if (route.show !== undefined && route.show === false) {
      continue;
    } else {
      newRoutes.push(route);
      if (Array.isArray(route.childRoutes)) {
        newRoutes.push(...flattenRoutes(route.childRoutes));
      }
    }
  }
  return newRoutes;
}

let debounceTimeout: NodeJS.Timeout;
export function debounce(fn: Function, delay: number): Function {
  return function (this: unknown, ...args: any[]) {
    console.log(debounceTimeout);
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    clearTimeout(debounceTimeout);
    debounceTimeout = setTimeout(() => {
      fn.apply(that, args);
    }, delay);
  };
}

const charMap: Map<string, string> = new Map([
  ['&', '&amp;'],
  ['<', '&lt;'],
  ['>', '&gt;'],
  ["'", '&#039;'],
  ['"', '&quot;'],
  ['/', '&#047;'],
]);
export function char2entity(s: string) {
  if (!s || typeof s === 'undefined') return s;
  charMap.forEach((value, key) => {
    s = s.replace(new RegExp(value, 'g'), key);
  });
  return s;
}

export function setCookie(
  cookieName: string,
  cookieValue: string,
  expireTime = 20_000
) {
  const date = new Date();
  date.setTime(date.getTime() + expireTime);
  // non-uic env, should set cookie in top domain
  // classic and new ui share this cookie
  const cookieDomain = isInUIC()
    ? ''
    : 'domain=' + (window as any).COOKIE_DOMAIN;
  document.cookie =
    cookieName +
    '=' +
    encodeURIComponent(cookieValue) +
    ';expires=' +
    date.toUTCString() +
    ';path=/;' +
    cookieDomain +
    ';Secure;SameSite=None';
}

export function getProvisionCookie(cookieName: string) {
  const name = cookieName + '=';
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(';');
  for (let c of ca) {
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return '';
}

export function deleteCookie(cookieKeyword: string) {
  const keys = document.cookie.match(/[^ =;]+(?=)/g);
  if (keys) {
    for (let i = keys.length; i--; i >= 0) {
      if (cookieKeyword && keys[i].indexOf(cookieKeyword) < 0) {
        continue;
      }
      document.cookie =
        keys[i] +
        '=0;expires=' +
        new Date(0).toUTCString() +
        ';path=/;' +
        'domain=' +
        (window as any).COOKIE_DOMAIN;
      document.cookie =
        keys[i] + '=0;expires=' + new Date(0).toUTCString() + ';path=/';
    }
  }
}

export function joinStringArr(arr: string[], language: string) {
  const delimiter = language === 'ja' ? '、' : ', ';
  return arr.join(delimiter);
}

export function numberInputOnWheelPreventChange(
  e: React.WheelEvent<HTMLInputElement>
) {
  e.currentTarget?.blur();
}

export function logoutCleanUp() {
  sessionStorage.clear();
  localStorage.removeItem('hasWhatsNewShowed');
  localStorage.removeItem('casUpgradeModalStatus');
}

/**
 * @description: Calculate the unit based on file size. If the backend passes a string, return it directly. Otherwise, calculate the unit based on the size.
 * @example: calcUnit(1024) => 1KB / calcUnit('1MB') => 1MB
 */
export const calcUnit = (xsize: number | string) => {
  if (!xsize) return '';
  if (typeof xsize === 'string') return xsize;
  let ret = '';
  if (xsize > 1024 * 1024) {
    ret = (xsize / (1024 * 1024)).toFixed(2) + 'MB';
  } else if (xsize > 1024) {
    ret = (xsize / 1024).toFixed(2) + 'KB';
  } else {
    ret = xsize + 'B';
  }
  return ret;
};

function neverValueGuard(v: never) {
  console.log(v);
}

/**
 * Safely parse localStorage data with error handling
 * @param key - localStorage key
 * @param defaultValue - default value if parsing fails
 * @returns parsed data or default value
 */
const safeParseLocalStorage = <T>(key: string, defaultValue: T): T => {
  try {
    const item = localStorage.getItem(key);
    if (!item) {
      return defaultValue;
    }
    return JSON.parse(item) as T;
  } catch (error) {
    console.error(`Failed to parse localStorage key "${key}":`, error);
    return defaultValue;
  }
};

/**
 * Safely get string from localStorage
 * @param key - localStorage key
 * @param defaultValue - default value if key doesn't exist
 * @returns string value or default value
 */
const getLocalStorageString = (key: string, defaultValue = ''): string => {
  try {
    return localStorage.getItem(key) || defaultValue;
  } catch (error) {
    console.error(`Failed to get localStorage key "${key}":`, error);
    return defaultValue;
  }
};

export default {
  compareDesc,
  compareAsc,
  toolTipString,
  urlMask,
  hash,
  validateEmail,
  validateIPv4,
  fillZeroToString,
  withEditPermission,
  withReadPermission,
  flattenRoutes,
  exportFile,
  char2entity,
  parseCSVToJSON,
  getProvisionCookie,
  isInUIC,
  numberInputOnWheelPreventChange,
  neverValueGuard,
  getLocalStorageString,
  safeParseLocalStorage,
};
